import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { getCASHeatsForSelector } from "@/pages/caster/data-entry/repositories/caster.repositories";
import { getLMFHeatsForSelector } from "@/pages/lmf/data-entry/repositories/lmf.repositories";
import { LADLE_CAR_COLORS } from "@/pages/lmf/data-entry/constants/ladleCarColors";
import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import {
  Box,
  CircularProgress,
  Grid,
  TextField,
  ToggleButton,
  Typography,
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

interface Props {
  show: boolean;
  onHide: (arrive: boolean) => void;
  assetId: number;
  changeHeatStatus: (
    heatId: number,
    newStatus: string,
    ladleCar?: number,
  ) => Promise<void>;
}

interface HeatToArrive {
  HeatId: number | null;
  HeatName: string;
}

export const HeatSelector = ({
  show,
  onHide,
  changeHeatStatus,
  assetId,
}: Props): JSX.Element => {
  const [heats, setHeats] = useState<HeatToArrive[]>([]);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ladleCar, setLadleCar] = useState<number | null>(null);

  const { handleSubmit, control, reset } = useForm({
    defaultValues: { HeatId: null },
  });

  const onSubmit: SubmitHandler<Omit<HeatToArrive, "HeatName">> = async (
    data,
  ) => {
    setIsSubmitLoading(true);
    if (data.HeatId !== null) {
      if (assetId === 12) {
        await changeHeatStatus(data.HeatId, "L", ladleCar ?? undefined);
      } else {
        await changeHeatStatus(data.HeatId, "C");
      }
      onHide(true);
      setIsSubmitLoading(false);
    }
  };

  const getHeats = useCallback(async () => {
    setIsDataLoading(true);
    try {
      const resp =
        assetId === 12
          ? await getLMFHeatsForSelector()
          : await getCASHeatsForSelector();
      if (resp.ok) {
        const heats = resp.data ?? [];
        setHeats(heats);
      } else {
        setError(resp.message!);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An error occurred while loading operators",
      );
    } finally {
      setIsDataLoading(false);
    }
  }, [assetId]);

  useEffect(() => {
    if (show) {
      getHeats();
      reset({ HeatId: null });
      setLadleCar(null);
    }
  }, [show, reset]);

  return (
    <>
      <MesfModal
        title="SELECT HEAT"
        open={show}
        handleClose={() => onHide(false)}
        id="select-heat-modal"
        maxWidth="sm"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <MesfModal.Content dividers style={{ padding: "15px 30px" }}>
            <Grid container spacing={1}>
              <Grid item xs={12} md={12}>
                <Controller
                  name="HeatId"
                  control={control}
                  rules={{ required: "Heat is required" }}
                  render={({ field, fieldState: { error } }) => (
                    <Autocomplete
                      id="clear-on-escape"
                      clearOnEscape
                      options={heats ?? []}
                      getOptionLabel={(option) => option.HeatName}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Heat"
                          variant="outlined"
                          fullWidth
                          error={!!error}
                          size="small"
                          helperText={error ? error.message : null}
                          slotProps={{
                            input: {
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {isDataLoading ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : (
                                    params.InputProps.endAdornment
                                  )}
                                </>
                              ),
                            },
                          }}
                        />
                      )}
                      value={
                        heats.find((heat) => heat.HeatId === field.value) ||
                        null
                      }
                      onChange={(event, newValue) => {
                        field.onChange(newValue ? newValue.HeatId : null);
                      }}
                    />
                  )}
                />
              </Grid>
              {assetId === 12 && (
                <Grid item xs={12}>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Ladle Car
                    </Typography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <ToggleButton
                          value={1}
                          selected={ladleCar === 1}
                          onChange={() => setLadleCar(1)}
                          fullWidth
                          sx={{
                            backgroundColor:
                              ladleCar === 1
                                ? `${LADLE_CAR_COLORS.lc1.border} !important`
                                : LADLE_CAR_COLORS.lc1.bg,
                            color: "#000",
                            fontWeight: 600,
                            border: `1px solid ${LADLE_CAR_COLORS.lc1.border}`,
                            borderRadius: "4px !important",
                            "&:hover": {
                              backgroundColor: LADLE_CAR_COLORS.lc1.hover,
                            },
                          }}
                        >
                          LC 1
                        </ToggleButton>
                      </Grid>
                      <Grid item xs={6}>
                        <ToggleButton
                          value={2}
                          selected={ladleCar === 2}
                          onChange={() => setLadleCar(2)}
                          fullWidth
                          sx={{
                            backgroundColor:
                              ladleCar === 2
                                ? `${LADLE_CAR_COLORS.lc2.border} !important`
                                : LADLE_CAR_COLORS.lc2.bg,
                            color: "#000",
                            fontWeight: 600,
                            border: `1px solid ${LADLE_CAR_COLORS.lc2.border}`,
                            borderRadius: "4px !important",
                            "&:hover": {
                              backgroundColor: LADLE_CAR_COLORS.lc2.hover,
                            },
                          }}
                        >
                          LC 2
                        </ToggleButton>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              )}
            </Grid>
          </MesfModal.Content>
          <MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
            <FooterModalButtons
              isSubmitLoading={isSubmitLoading}
              onHide={onHide}
              disabled={assetId === 12 && ladleCar === null}
            />
          </MesfModal.Actions>
        </form>
      </MesfModal>
      <ErrorModal
        error={error}
        onHide={() => setError("")}
        title="Error loading Operators"
      />
    </>
  );
};
